import { effect, Signal, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { hashKey } from '@tanstack/query-core';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { queryOptions, QueryObserver } from '@tanstack/react-query';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { compareKey, stringifyKey, TableClient } from '@thinkalpha/table-client';
import type { Bounds, RowUpdate, SortModel } from '@thinkalpha/table-client';
import { deferred } from '@thinkalpha/ui-core/deferred';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { shortid } from '@thinkalpha/ui-core/shortid';
import { isEqual } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { optional } from 'inversify';
import { jwtDecode } from 'jwt-decode';
import {
    BehaviorSubject,
    combineLatest,
    concat,
    connectable,
    distinctUntilChanged,
    filter,
    from,
    map,
    NEVER,
    of,
    ReplaySubject,
    skip,
    switchMap,
} from 'rxjs';
import type { Connectable, Observable, ObservedValueOf } from 'rxjs';
import { getSnapshot } from 'src/api/getTableSnapshot';
import type { TableStatus } from 'src/components/table-view/model';
import { inject, injectable, ReactiveInjectable, reacts } from 'src/features/ioc';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { enableEnhancedTableProgress } from 'src/lib/featureFlags/flags/enableEnhancedTableProgress';
import { getTableCreationRequestTiming } from 'src/lib/tableCreationRequestTiming';
import { getSpecialTableQueryKeys, NEW_TABLE_RESET } from 'src/models/TableModel';
import type {
    TableConstructionProgress,
    TableCreationOptions,
    TableModel,
    SignalUpdate,
    OptimizedTableFilter,
    TableQueryFunction,
} from 'src/models/TableModel';
import { getTableConstructionProgress, isTableConstructionActive } from 'src/models/TableModel/constructionProgress';
import { EndOfDataRecoveryLogAggregator } from 'src/models/TableModel/endOfDataRecoveryLogAggregator';
import type {
    EndOfDataRecoveryCancellationReason,
    EndOfDataRecoveryRetryReason,
} from 'src/models/TableModel/endOfDataRecoveryLogAggregator';
import { TableStartupLatency } from 'src/models/TableModel/tableStartupLatency';
import type { TableStartupTiming } from 'src/models/TableModel/tableStartupLatency';
import type { TableProxyTarget } from 'src/services/ProxyClientService';
import type { FullKeyType } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

/**
 * Key used to mark query results that came from the optimization path.
 * When the wrapped queryFn returns an optimized result (sort/filter amendment),
 * it wraps the post-select result with this key so the wrapped select function
 * can detect it and bypass transformation.
 *
 * Note: We use a string key instead of a Symbol because React Query's structural
 * sharing strips Symbols when cloning query results.
 */
const OptimizedQueryResult = '__optimizedQueryResult__' as const;

interface OptimizedResultWrapper {
    [OptimizedQueryResult]: true;
    result: FullKeyType;
}

function isOptimizedResult(value: unknown): value is OptimizedResultWrapper {
    return typeof value === 'object' && value !== null && OptimizedQueryResult in value;
}

type ConnectableStream<T> = Observable<T> & { connect(): { unsubscribe(): void } };

function isConnectableStream<T>(stream: Observable<T>): stream is ConnectableStream<T> {
    return 'connect' in stream && typeof stream.connect === 'function';
}

class TableClientEnvelope implements Disposable {
    #tsIdConnection: { unsubscribe(): void } | undefined;

    constructor(
        readonly tableClient: TableClient,
        tsIdConnection: { unsubscribe(): void } | undefined,
        readonly startupLatency: TableStartupLatency | undefined,
    ) {
        this.#tsIdConnection = tsIdConnection;
    }

    [Symbol.dispose]() {
        this.startupLatency?.[Symbol.dispose]();
        this.#tsIdConnection?.unsubscribe();
        this.tableClient[Symbol.dispose]();
    }
}

/**
 * Internal query options type that includes OptimizedResultWrapper.
 * The wrapper is used internally to bypass select transformation for optimized results.
 */
export type InternalQueryOptions = QueryObserverOptions<
    FullKeyType | OptimizedResultWrapper | undefined,
    any,
    FullKeyType | undefined
>;

type NonNullFullKeyType = Exclude<FullKeyType, null>;

export interface TableState {
    columns: OutputColumnWithMetadata[];
    error: Error | undefined;
    queryOptions: InternalQueryOptions;
    tableCreationResult: FullKeyType;
    isAmendableSortTableCreationResult: boolean;
    isAmendableFilterTableCreationResult: boolean;
    proxyTarget: TableProxyTarget | null;
    proxyStatus: TableStatus;
    tableStatus: TableStatus;
    tableClientSpec: TableClientSpec | null;
    serverUUID: string | null;
}

interface TableQueryTiming {
    readonly queryFnStartedAt: number | undefined;
    readonly queryFnCompletedAt: number | undefined;
    readonly tableRequestStartedAt: number | undefined;
    readonly tableRequestCompletedAt: number | undefined;
    readonly tableKnownAt: number;
}

export interface TableClientSpec {
    proxyTarget: TableProxyTarget;
    tableCreationResult: NonNullFullKeyType;
    tableQueryTiming?: TableQueryTiming;
}

export type TableStateAction =
    | { type: 'clearTable' }
    | { type: 'enterErroredState'; error: Error }
    | {
          type: 'receiveTableCreationResult';
          tableCreationResult: NonNullFullKeyType;
          tableQueryTiming?: TableQueryTiming;
      }
    | { type: 'recycleTableClient'; tableCreationResult: NonNullFullKeyType; tableQueryTiming?: TableQueryTiming }
    | { type: 'updateProxyConnectionStatus'; status: TableStatus }
    | { type: 'updateProxyTarget'; target: TableProxyTarget }
    | { type: 'updateQueryOptions'; queryOptions: InternalQueryOptions }
    | { type: 'updateTableConnectionStatus'; status: TableStatus }
    | { type: 'updateServerUUID'; serverUUID: string };

const DEFAULT_THROTTLE_MS = 1;

export const END_OF_DATA_RECOVERY_BASE_DELAY_MS = 1_000;
export const END_OF_DATA_RECOVERY_MAX_DELAY_MS = 60_000;
export const END_OF_DATA_RECOVERY_STABLE_MS = 30_000;
const END_OF_DATA_RECOVERY_JITTER_RATIO = 0.2;

/**
 * Delay before the next recovery after `completedAttempts` consecutive attempts have failed to
 * produce a healthy subscription. The first end-of-data recovery is deliberately immediate; this
 * function is used only for subsequent attempts. Jitter prevents a proxy-wide end-of-data sweep
 * from keeping every table in lockstep against the subscription rate limiter.
 */
export const getEndOfDataRecoveryDelay = (completedAttempts: number, randomValue = Math.random()): number => {
    const maxExponent = Math.ceil(Math.log2(END_OF_DATA_RECOVERY_MAX_DELAY_MS / END_OF_DATA_RECOVERY_BASE_DELAY_MS));
    const exponent = Math.min(Math.max(0, completedAttempts - 1), maxExponent);
    const exponentialDelay = Math.min(
        END_OF_DATA_RECOVERY_BASE_DELAY_MS * 2 ** exponent,
        END_OF_DATA_RECOVERY_MAX_DELAY_MS,
    );
    const jitterMultiplier =
        1 - END_OF_DATA_RECOVERY_JITTER_RATIO + randomValue * END_OF_DATA_RECOVERY_JITTER_RATIO * 2;
    return Math.min(END_OF_DATA_RECOVERY_MAX_DELAY_MS, Math.round(exponentialDelay * jitterMultiplier));
};

const checkIfAmendAllowedInTableCookie = (tableCookie: string | undefined, type: 'view' | 'index'): boolean => {
    if (!tableCookie) {
        return false;
    }

    try {
        const decoded = jwtDecode(tableCookie) as { permitsAmend?: boolean | 'view' | 'index' };
        return decoded.permitsAmend === true || decoded.permitsAmend === type;
    } catch {
        return false;
    }
};

const clearTableState = (state: TableState): TableState => ({
    ...state,
    columns: [],
    error: undefined,
    isAmendableSortTableCreationResult: false,
    isAmendableFilterTableCreationResult: false,
    tableCreationResult: null,
    tableClientSpec: null,
    serverUUID: null,
    tableStatus: 'uninitialized',
});

export const tableStateReducer = (state: TableState, action: TableStateAction): TableState => {
    switch (action.type) {
        case 'clearTable':
            return clearTableState(state);
        case 'enterErroredState':
            return {
                ...clearTableState(state),
                error: action.error,
            };
        case 'receiveTableCreationResult': {
            const { tableCreationResult } = action;
            const columns = 'columns' in tableCreationResult ? tableCreationResult.columns : [];

            // The key may be the same if the user is sorting and the existing table is reconfigured
            if (compareKey(state.tableCreationResult?.key, tableCreationResult.key)) {
                // Same-key refetches (window-focus refetch, end-of-data recovery) usually return
                // identical columns; preserving the array identity keeps grids from tearing down
                // and rebuilding their column state over a no-op result.
                if (isEqual(state.columns, columns)) {
                    return state;
                }
                return {
                    ...state,
                    columns,
                };
            }

            return {
                ...state,
                columns,
                error: undefined,
                isAmendableSortTableCreationResult: checkIfAmendAllowedInTableCookie(
                    tableCreationResult.tableCookie,
                    'index',
                ),
                isAmendableFilterTableCreationResult: checkIfAmendAllowedInTableCookie(
                    tableCreationResult.tableCookie,
                    'view',
                ),
                tableCreationResult,
                tableClientSpec: state.proxyTarget
                    ? {
                          proxyTarget: state.proxyTarget,
                          tableCreationResult,
                          ...(action.tableQueryTiming ? { tableQueryTiming: action.tableQueryTiming } : {}),
                      }
                    : null,
                // Reset server UUID when table changes - we'll get a new one from the observable
                serverUUID: null,
            };
        }
        case 'recycleTableClient': {
            if (!state.tableClientSpec || !state.proxyTarget) {
                return state;
            }
            // A fresh spec identity makes the lifecycle sync tear down the current TableClient and
            // build a replacement, establishing a new proxy subscription for the same table key.
            // Used when the proxy reports end-of-data for the current subscription: the key is
            // still valid but the subscription will never produce data again. The refetched
            // creation result is adopted wholesale so the replacement subscribes with the newest
            // table cookie; column identity is preserved when the content is unchanged.
            const { tableCreationResult } = action;
            const columns = 'columns' in tableCreationResult ? tableCreationResult.columns : [];
            return {
                ...state,
                columns: isEqual(state.columns, columns) ? state.columns : columns,
                error: undefined,
                isAmendableSortTableCreationResult: checkIfAmendAllowedInTableCookie(
                    tableCreationResult.tableCookie,
                    'index',
                ),
                isAmendableFilterTableCreationResult: checkIfAmendAllowedInTableCookie(
                    tableCreationResult.tableCookie,
                    'view',
                ),
                tableCreationResult,
                serverUUID: null,
                tableClientSpec: {
                    proxyTarget: state.proxyTarget,
                    tableCreationResult,
                    ...(action.tableQueryTiming ? { tableQueryTiming: action.tableQueryTiming } : {}),
                },
            };
        }
        case 'updateProxyConnectionStatus':
            if (action.status === state.proxyStatus) {
                return state;
            }
            return {
                ...state,
                proxyStatus: action.status,
            };
        case 'updateProxyTarget':
            if (action.target.generation === state.proxyTarget?.generation) {
                return state;
            }
            // The target owns transport for an existing table; it is not part of table-creation
            // identity. Preserve the TCR and query state, and replace only target-bound state.
            return {
                ...state,
                proxyTarget: action.target,
                serverUUID: state.tableClientSpec ? null : state.serverUUID,
                tableClientSpec: state.tableCreationResult
                    ? {
                          proxyTarget: action.target,
                          tableCreationResult: state.tableCreationResult,
                      }
                    : null,
            };
        case 'updateQueryOptions':
            return {
                ...state,
                queryOptions: action.queryOptions,
            };
        case 'updateTableConnectionStatus':
            if (action.status === state.tableStatus) {
                return state;
            }
            return {
                ...state,
                tableStatus: action.status,
            };
        case 'updateServerUUID':
            if (action.serverUUID === state.serverUUID) {
                return state;
            }
            return {
                ...state,
                serverUUID: action.serverUUID,
            };
        default:
            return state;
    }
};

export const createInitialTableState = (): TableState => ({
    columns: [],
    error: undefined,
    isAmendableSortTableCreationResult: false,
    isAmendableFilterTableCreationResult: false,
    proxyTarget: null,
    queryOptions: queryOptions({ queryKey: ['EMPTY_TABLE'], queryFn: () => null }),
    tableCreationResult: null,
    tableClientSpec: null,
    proxyStatus: 'uninitialized',
    tableStatus: 'uninitialized',
    serverUUID: null,
});

@injectable()
export class TableModelImpl extends ReactiveInjectable implements TableModel {
    #bounds!: Bounds;

    get bounds() {
        return this.#bounds;
    }

    #columnByIdMap = new Map<string, OutputColumnWithMetadata>();

    #tableClientQueryOptionsMap = new WeakMap<TableClient, InternalQueryOptions>();

    readonly #tableClientBox = this.disposableStack.use(new DisposableBox<TableClientEnvelope>());

    #queryOptionsSignal!: Signal<QueryObserverOptions<any, any, FullKeyType | undefined>>;

    get columns() {
        return this.#state.columns;
    }

    #columnsSignal: Signal<OutputColumnWithMetadata[]> = signal([]);
    get columnsSignal(): ReadonlySignal<OutputColumnWithMetadata[]> {
        return this.#columnsSignal;
    }

    #countConnected$: Connectable<number>;
    #count$: Observable<number>;

    get count$() {
        return this.#count$;
    }

    #progressConnected$: Connectable<TableConstructionProgress>;
    #progress$: Observable<TableConstructionProgress>;
    #progressSignal = signal<TableConstructionProgress>(undefined);

    get progress$() {
        return this.#progress$;
    }

    get progress() {
        return this.#progressSignal;
    }

    #connectionStatusSignal = signal<TableStatus>('uninitialized');

    get connectionStatus(): ReadonlySignal<TableStatus> {
        return this.#connectionStatusSignal;
    }

    #failureCountSignal = signal<number>(0);
    #destroyed = false;

    get failureCount(): ReadonlySignal<number> {
        return this.#failureCountSignal;
    }

    #keySignal = signal<string>('');

    get keySignal(): ReadonlySignal<string> {
        return this.#keySignal;
    }

    // Track the last query key that resulted in a successful table creation
    // Used by the wrapped queryFn to determine if only sort/filter changed
    #lastSuccessfulQueryKey: readonly unknown[] | undefined;

    #debugIdentifier: string | undefined;

    #completedQueryFnTimings = new Map<
        string,
        Pick<
            TableQueryTiming,
            'queryFnStartedAt' | 'queryFnCompletedAt' | 'tableRequestStartedAt' | 'tableRequestCompletedAt'
        >
    >();

    #endOfDataRecoveryQueryTiming: { epoch: number; timing: TableQueryTiming } | undefined;

    /**
     * Wraps a queryFn with optimization logic that can bypass network requests
     * when only the sort model and/or filter changes and the existing table supports amending.
     *
     * This approach ensures the QueryObserver always has the correct query options,
     * which is important for reconnect scenarios where the QueryObserver needs to
     * refetch with the latest configuration.
     */
    #wrapQueryFn(
        originalQueryFn: QueryObserverOptions<FullKeyType | undefined>['queryFn'],
        newQueryKey: readonly unknown[] | undefined,
    ): InternalQueryOptions['queryFn'] {
        // queryFn can be undefined or a skipToken symbol - only wrap actual functions
        if (!originalQueryFn || typeof originalQueryFn !== 'function') {
            return originalQueryFn;
        }

        const originalFn: TableQueryFunction<FullKeyType | undefined> = originalQueryFn;
        return async (context) => {
            const currentTableClient = this.tableClient;
            const currentTableCreationResult = this.#state.tableCreationResult;
            const lastQueryKey = this.#lastSuccessfulQueryKey;

            // Check if we can optimize (sort/filter-only change on an amendable table)
            if (currentTableClient && currentTableCreationResult && lastQueryKey && newQueryKey) {
                const optimization = this.#checkOptimization(lastQueryKey, newQueryKey);

                if (optimization.canOptimize) {
                    if (TRACE) {
                        this.#log.trace('Optimizing via wrapped queryFn', {
                            sortChanged: optimization.sortChanged,
                            filterChanged: optimization.filterChanged,
                            newSortModel: optimization.newSortModel,
                            newFilter: optimization.newFilter,
                        });
                    }

                    // Apply sort if it changed
                    if (optimization.sortChanged) {
                        const sort: SortModel = isEmpty(optimization.newSortModel) ? {} : optimization.newSortModel;
                        currentTableClient.sort(sort, v4());
                    }

                    // Apply filter if it changed
                    if (optimization.filterChanged) {
                        if (!optimization.newFilter) {
                            // Clear filter
                            currentTableClient.filter('', v4(), false);
                        } else {
                            currentTableClient.filter(
                                optimization.newFilter.filter,
                                v4(),
                                optimization.newFilter.type === 'raw-formula',
                            );
                        }
                    }

                    // Update tracking and return existing result (no network call)
                    this.#lastSuccessfulQueryKey = newQueryKey;

                    if (TRACE) {
                        this.#log.trace('returning current TCR (optimized)', {
                            newQueryKey,
                            currentTableCreationResult,
                            state: this.#state,
                        });
                    }

                    // Wrap the result so the select function knows to bypass transformation.
                    // The post-select tableCreationResult is already what we want, so we mark it
                    // to prevent select from running on it again.
                    return {
                        [OptimizedQueryResult]: true,
                        result: currentTableCreationResult,
                    } satisfies OptimizedResultWrapper;
                }
            }

            // Can't optimize - go to network
            if (TRACE) {
                this.#log.trace('Cannot optimize, calling original queryFn');
            }
            const queryHash = hashKey(newQueryKey ?? []);
            this.#completedQueryFnTimings.delete(queryHash);
            const queryFnStartedAt = performance.now();
            const result = await originalFn(context, this.#debugIdentifier);
            const queryFnCompletedAt = performance.now();
            const tableRequestTiming = getTableCreationRequestTiming(result);

            // Track successful query key for future optimization comparisons
            if (result) {
                this.#lastSuccessfulQueryKey = newQueryKey;
                this.#completedQueryFnTimings.set(queryHash, {
                    queryFnStartedAt,
                    queryFnCompletedAt,
                    tableRequestStartedAt: tableRequestTiming?.requestStartedAt,
                    tableRequestCompletedAt: tableRequestTiming?.requestCompletedAt,
                });
            }

            return result;
        };
    }

    /**
     * Checks if we can optimize by updating sort/filter on the existing table
     * instead of making a network request.
     */
    #checkOptimization(
        oldKey: readonly unknown[],
        newKey: readonly unknown[],
    ): {
        canOptimize: boolean;
        sortChanged: boolean;
        filterChanged: boolean;
        newSortModel: SortModel;
        newFilter: OptimizedTableFilter | undefined;
    } {
        const { sort: oldSortModel, filter: oldFilter, rest: oldUnspecialKey } = getSpecialTableQueryKeys(oldKey);
        const { sort: newSortModel, filter: newFilter, rest: newUnspecialKey } = getSpecialTableQueryKeys(newKey);

        // Check if only sort/filter changed (everything else must be identical)
        const keysMatch =
            oldUnspecialKey.length === newUnspecialKey.length &&
            oldUnspecialKey.every((item, index) => {
                return JSON.stringify(item) === JSON.stringify(newUnspecialKey[index]);
            });

        if (!keysMatch) {
            return {
                canOptimize: false,
                sortChanged: false,
                filterChanged: false,
                newSortModel: newSortModel || {},
                newFilter,
            };
        }

        const sortChanged = JSON.stringify(oldSortModel) !== JSON.stringify(newSortModel);
        let filterChanged = JSON.stringify(oldFilter) !== JSON.stringify(newFilter);

        // Don't optimize formula type filters
        if (filterChanged && newFilter?.type === 'formula') {
            if (TRACE) {
                this.#log.trace('Skipping filter optimization for formula type');
            }
            filterChanged = false;
        }

        // Determine if we can actually optimize based on permissions
        const canOptimizeSort = sortChanged && this.#state.isAmendableSortTableCreationResult;
        const canOptimizeFilter = filterChanged && this.#state.isAmendableFilterTableCreationResult;

        // We can optimize if:
        // - Only sort changed and we can amend sort
        // - Only filter changed and we can amend filter
        // - Both changed and we can amend both
        let canOptimize = false;
        if (sortChanged && filterChanged) {
            canOptimize = canOptimizeSort && canOptimizeFilter;
        } else if (sortChanged) {
            canOptimize = canOptimizeSort;
        } else if (filterChanged) {
            canOptimize = canOptimizeFilter;
        }

        if (TRACE && !canOptimize && (sortChanged || filterChanged)) {
            this.#log.trace('Skipping optimization due to permission mismatch', {
                sortChanged,
                filterChanged,
                isAmendableSortTableCreationResult: this.#state.isAmendableSortTableCreationResult,
                isAmendableFilterTableCreationResult: this.#state.isAmendableFilterTableCreationResult,
            });
        }

        return {
            canOptimize,
            sortChanged: canOptimize && sortChanged,
            filterChanged: canOptimize && filterChanged,
            newSortModel: newSortModel || {},
            newFilter,
        };
    }

    /**
     * Wraps a select function to detect optimized results and bypass transformation.
     */
    #wrapSelect(
        originalSelect: QueryObserverOptions<FullKeyType | undefined>['select'],
    ): InternalQueryOptions['select'] {
        const select = originalSelect ?? ((data: FullKeyType | undefined) => data);
        return (data) => {
            if (isOptimizedResult(data)) {
                return data.result;
            }
            return select(data);
        };
    }

    /**
     * Creates query options with a wrapped queryFn for optimization.
     */
    #createOptimizedQueryOptions(options: QueryObserverOptions<FullKeyType | undefined>): InternalQueryOptions {
        return {
            ...options,
            queryFn: this.#wrapQueryFn(options.queryFn, options.queryKey),
            select: this.#wrapSelect(options.select),
        };
    }

    #updateConnectionStatusSignal() {
        const { tableStatus, proxyStatus } = this.#state;
        let newStatus: TableStatus = 'uninitialized';

        if (tableStatus === 'uninitialized' || proxyStatus === 'uninitialized') {
            newStatus = 'uninitialized';
        } else if (tableStatus === 'disconnected' || proxyStatus === 'disconnected') {
            newStatus = 'disconnected';
        } else if (tableStatus === 'connecting' || proxyStatus === 'connecting') {
            newStatus = 'connecting';
        } else if (tableStatus === 'connected' && proxyStatus === 'connected') {
            newStatus = 'connected';
        }

        if (this.#connectionStatusSignal.value !== newStatus) {
            this.#connectionStatusSignal.value = newStatus;
        }
    }

    constructor(
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('ProxyClientService') private proxyClientService: ReactBindings['ProxyClientService'],
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('Store') private store: ReactBindings['Store'],
        // FeatureFlagService is intentionally absent from worker containers. Those
        // contexts retain the flag's default legacy percentage-only behavior.
        @inject('FeatureFlagService')
        @optional()
        featureFlagService?: ReactBindings['FeatureFlagService'] | undefined,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.#id = shortid();
        this.#log = logger.getSubLogger({ name: `TableModel:${this.#id}` }, { component: 'table' });
        this.#endOfDataRecoveryLog = this.disposableStack.use(new EndOfDataRecoveryLogAggregator(this.#log, this.#id));
        this.disposableStack.defer(() => this.#resetEndOfDataRecovery('model-disposed'));

        const enhancedTableProgressEnabled$ = new BehaviorSubject(enableEnhancedTableProgress.defaultValue);
        this.disposableStack.defer(() => enhancedTableProgressEnabled$.complete());
        if (featureFlagService) {
            const enhancedTableProgressEnabled = featureFlagService.getSignal(enableEnhancedTableProgress);
            this.disposableStack.defer(
                effect(() => {
                    enhancedTableProgressEnabled$.next(enhancedTableProgressEnabled.value);
                }),
            );
        }

        this.#updateConnectionStatusSignal();

        // Transport selection enters the model declaratively. The reducer decides whether the target
        // changed and drops only the target-bound TableClient state. The existing TCR remains valid,
        // so lifecycle code can immediately materialize its replacement against the new raw client.
        this.disposableStack.defer(
            effect(() => {
                const target = this.proxyClientService.target.value;
                untracked(() => {
                    if (this.#state.proxyTarget && target.generation !== this.#state.proxyTarget.generation) {
                        this.#resetEndOfDataRecovery('proxy-target-changed');
                    }
                    this.#takeStateAction({ type: 'updateProxyTarget', target });
                });
            }),
        );

        // Proxy connection status is derived by ProxyClientService from whichever raw client is
        // active. It is independent of target generation and table-creation status.
        this.disposableStack.defer(
            effect(() => {
                const status = this.proxyClientService.connectionStatus.value ?? 'uninitialized';
                untracked(() => {
                    this.#takeStateAction({ type: 'updateProxyConnectionStatus', status });
                });
            }),
        );

        // The active TableClient envelope owns the manual tsId$ connection. This subscription only
        // follows the current client to publish its server UUID into model state.
        const sub = from(this)
            .pipe(
                map(() => this.tableClient),
                distinctUntilChanged(),
                switchMap((tableClient) => {
                    if (!tableClient) {
                        if (TRACE) {
                            this.#log.trace({
                                message: 'No TableClient available for server UUID tracking',
                            });
                        }
                        return NEVER;
                    }

                    if (TRACE) {
                        this.#log.trace({ message: 'Setting up tsId$ subscription for server UUID' });
                    }

                    return tableClient.tsId$;
                }),
            )
            .subscribe((tsId) => {
                if (tsId) {
                    if (TRACE) {
                        this.#log.trace({
                            message: 'Received server UUID from tsId$',
                            tsId,
                        });
                    }
                    this.#takeStateAction({ type: 'updateServerUUID', serverUUID: tsId });
                } else if (TRACE) {
                    this.#log.trace({ message: 'Empty tsId received' });
                }
            });
        this.disposableStack.use(sub);

        // A descriptor proves that the replacement subscription exists on the proxy and has
        // reached its TableServer. Until then, another end-of-data is treated as a failed recovery
        // and receives exponential backoff.
        const recoveryHealthSub = from(this)
            .pipe(
                map(() => this.tableClient),
                distinctUntilChanged(),
                switchMap((tableClient) => {
                    if (!tableClient) return NEVER;
                    return tableClient.descriptor$.pipe(map(() => tableClient));
                }),
            )
            .subscribe((tableClient) => {
                this.#markEndOfDataRecoveryHealthy(tableClient, 'descriptor');
            });
        this.disposableStack.use(recoveryHealthSub);

        // Subscribe to end-of-data events from TableClient
        const endOfDataSub = from(this)
            .pipe(
                map(() => this.tableClient),
                distinctUntilChanged(),
                switchMap((tableClient) => {
                    if (!tableClient) return NEVER;
                    return tableClient.endOfData$.pipe(map(() => tableClient));
                }),
            )
            .subscribe((tableClient) => {
                this.#onEndOfData(tableClient);
            });
        this.disposableStack.use(endOfDataSub);

        {
            this.disposableStack.use(
                from(this)
                    .pipe(
                        map(() => this.tableClient),
                        distinctUntilChanged(),
                        switchMap((tc) => concat(of(NEW_TABLE_RESET), tc ? tc.update$ : NEVER)),
                    )
                    .subscribe(this.#updateConnected$),
            );

            this.#updatesConnected$ = connectable(
                from(this).pipe(
                    map(() => this.tableClient),
                    distinctUntilChanged(),
                    switchMap((tc) => concat(of(NEW_TABLE_RESET), tc ? tc.updates$ : NEVER)),
                ),
                { connector: () => new ReplaySubject(1) },
            );

            this.disposableStack.use(this.#updatesConnected$.connect());

            this.#update$ = this.#updateConnected$.pipe(filter((update) => update !== NEW_TABLE_RESET));
            this.#updates$ = this.#updatesConnected$.pipe(filter((update) => update !== NEW_TABLE_RESET));
        }

        {
            this.#countConnected$ = connectable(
                from(this).pipe(
                    map(() => this.tableClient),
                    distinctUntilChanged(),
                    switchMap((tc) => concat(of(NEW_TABLE_RESET as unknown as number), tc ? tc.rowCount$ : NEVER)),
                ),
                { connector: () => new ReplaySubject(1) },
            );
            this.disposableStack.use(this.#countConnected$.connect());

            this.#count$ = this.#countConnected$.pipe(map((count) => ((count as any) === NEW_TABLE_RESET ? 0 : count)));
        }

        {
            this.#progressConnected$ = connectable(
                from(this).pipe(
                    map(() => this.tableClient),
                    distinctUntilChanged(),
                    switchMap((tc) =>
                        concat(
                            of(NEW_TABLE_RESET as unknown as TableConstructionProgress),
                            tc
                                ? combineLatest([tc.progressUpdate$, enhancedTableProgressEnabled$]).pipe(
                                      map(([update, enabled]) => getTableConstructionProgress(update, enabled)),
                                  )
                                : NEVER,
                        ),
                    ),
                ),
                { connector: () => new ReplaySubject(1) },
            );
            this.disposableStack.use(this.#progressConnected$.connect());

            this.#progress$ = this.#progressConnected$.pipe(
                map((progress) => ((progress as any) === NEW_TABLE_RESET ? undefined : progress)),
            );

            let lastProgressWarningTime: number | undefined;
            this.disposableStack.use(
                this.#progress$.subscribe((progress) => {
                    if (TRACE) {
                        this.#log.trace({
                            message: 'Table construction progress update',
                            progress,
                            tableKey: this.tableClient?.key,
                        });
                    }

                    if (isTableConstructionActive(progress)) {
                        const now = performance.now();
                        if (lastProgressWarningTime === undefined) {
                            // First progress message, just record the time
                            lastProgressWarningTime = now;
                        } else if (now - lastProgressWarningTime >= 5000) {
                            lastProgressWarningTime = now;
                            this.#log.warn({
                                message: 'Table construction still in progress after 5 seconds',
                                progress,
                                tableKey: this.tableClient?.key,
                            });
                        }
                    } else if (progress === 100 && lastProgressWarningTime !== undefined) {
                        this.#log.info({
                            message: 'Table construction complete',
                            tableKey: this.tableClient?.key,
                        });
                        lastProgressWarningTime = undefined;
                    }

                    if (this.#progressSignal.value !== progress) {
                        this.#progressSignal.value = progress;
                    }
                }),
            );
        }
    }

    destroy() {
        this.#destroyed = true;
        this.#resetEndOfDataRecovery('destroyed');
        this.#takeStateAction({ type: 'clearTable' });
    }

    refetch() {
        if (TRACE) {
            this.#log.trace({ message: 'refetch() called manually' });
        }
        if (this.#query) {
            this.#destroyed = false;
            this.#resetEndOfDataRecovery('manual-refetch');
            // Tear down the existing TableClient immediately so the UI shows
            // 'connecting' right away rather than keeping stale data visible.
            this.#takeStateAction({ type: 'clearTable' });
            // Reset the optimization tracking so the next fetch goes to the network.
            this.#lastSuccessfulQueryKey = undefined;
            // Reset the query cache and trigger a new fetch.
            this.queryClient.resetQueries({
                queryKey: this.#state.queryOptions.queryKey,
                exact: true,
            });
        }
    }

    reset() {
        this.#resetEndOfDataRecovery('manual-reset');
        this.#takeStateAction({ type: 'clearTable' });
    }

    #endOfDataRecoveryAttempts = 0;
    #endOfDataRecoveryCandidate: TableClient | null = null;
    #endOfDataRecoveryEpoch = 0;
    #endOfDataRecoveryInFlightEpoch: number | undefined;
    #endOfDataRecoveryRetryTimer: ReturnType<typeof setTimeout> | undefined;
    #endOfDataRecoveryStableTimer: ReturnType<typeof setTimeout> | undefined;

    #onEndOfData(tableClient: TableClient): void {
        if (tableClient !== this.tableClient || this.#destroyed || this.disposableStack.disposed) {
            return;
        }

        this.#clearEndOfDataRecoveryStableTimer();
        this.#endOfDataRecoveryCandidate = null;

        const tableKey = this.#state.tableCreationResult?.key
            ? stringifyKey(this.#state.tableCreationResult.key)
            : null;

        if (this.#endOfDataRecoveryInFlightEpoch === this.#endOfDataRecoveryEpoch) {
            this.#endOfDataRecoveryLog.update({ type: 'end-of-data-coalesced', reason: 'in-flight' });
            return;
        }

        if (this.#endOfDataRecoveryRetryTimer !== undefined) {
            this.#endOfDataRecoveryLog.update({ type: 'end-of-data-coalesced', reason: 'retry-scheduled' });
            return;
        }

        if (this.#endOfDataRecoveryAttempts === 0) {
            this.#endOfDataRecoveryLog.update(
                {
                    type: 'incident-started',
                    tableKey,
                    debugIdentifier: this.#debugIdentifier,
                },
                'before-and-after',
            );
            void this.#recoverFromEndOfData();
            return;
        }

        this.#endOfDataRecoveryLog.update({ type: 'replacement-end-of-data' });
        this.#scheduleEndOfDataRecovery('replacement-end-of-data');
    }

    #scheduleEndOfDataRecovery(reason: EndOfDataRecoveryRetryReason): void {
        if (
            this.#destroyed ||
            this.disposableStack.disposed ||
            this.#endOfDataRecoveryRetryTimer !== undefined ||
            this.#endOfDataRecoveryInFlightEpoch === this.#endOfDataRecoveryEpoch
        ) {
            return;
        }

        const epoch = this.#endOfDataRecoveryEpoch;
        const retryInMs = getEndOfDataRecoveryDelay(this.#endOfDataRecoveryAttempts);
        this.#endOfDataRecoveryLog.update({
            type: 'retry-scheduled',
            reason,
            attempt: this.#endOfDataRecoveryAttempts + 1,
            retryInMs,
        });

        this.#endOfDataRecoveryRetryTimer = setTimeout(() => {
            this.#endOfDataRecoveryRetryTimer = undefined;
            if (epoch !== this.#endOfDataRecoveryEpoch || this.#destroyed || this.disposableStack.disposed) {
                return;
            }
            void this.#recoverFromEndOfData();
        }, retryInMs);
    }

    #armEndOfDataRecoveryStableTimer(tableClient: TableClient): void {
        this.#clearEndOfDataRecoveryStableTimer();
        this.#endOfDataRecoveryCandidate = tableClient;
        const epoch = this.#endOfDataRecoveryEpoch;
        this.#endOfDataRecoveryStableTimer = setTimeout(() => {
            this.#endOfDataRecoveryStableTimer = undefined;
            if (epoch !== this.#endOfDataRecoveryEpoch) {
                return;
            }
            this.#markEndOfDataRecoveryHealthy(tableClient, 'stable');
        }, END_OF_DATA_RECOVERY_STABLE_MS);
    }

    #markEndOfDataRecoveryHealthy(tableClient: TableClient, evidence: 'descriptor' | 'stable'): void {
        if (tableClient !== this.#endOfDataRecoveryCandidate || tableClient !== this.tableClient) {
            return;
        }

        this.#clearEndOfDataRecoveryStableTimer();
        this.#endOfDataRecoveryCandidate = null;
        this.#endOfDataRecoveryAttempts = 0;
        this.#endOfDataRecoveryLog.update({ type: 'healthy', evidence }, 'before-and-after');
    }

    #clearEndOfDataRecoveryRetryTimer(): void {
        if (this.#endOfDataRecoveryRetryTimer !== undefined) {
            clearTimeout(this.#endOfDataRecoveryRetryTimer);
            this.#endOfDataRecoveryRetryTimer = undefined;
        }
    }

    #clearEndOfDataRecoveryStableTimer(): void {
        if (this.#endOfDataRecoveryStableTimer !== undefined) {
            clearTimeout(this.#endOfDataRecoveryStableTimer);
            this.#endOfDataRecoveryStableTimer = undefined;
        }
    }

    #resetEndOfDataRecovery(reason: EndOfDataRecoveryCancellationReason): void {
        this.#endOfDataRecoveryLog.update({ type: 'canceled', reason }, 'before-and-after');
        this.#endOfDataRecoveryEpoch += 1;
        this.#endOfDataRecoveryQueryTiming = undefined;
        this.#clearEndOfDataRecoveryRetryTimer();
        this.#clearEndOfDataRecoveryStableTimer();
        this.#endOfDataRecoveryCandidate = null;
        this.#endOfDataRecoveryAttempts = 0;
    }

    /**
     * The proxy emits end-of-data when its upstream table-server connection for our subscription
     * ends (PLAT-11525): the subscription will never produce data again, even though the table key
     * usually remains valid. Recovery refetches the table creation result; when the key comes back
     * unchanged the reducer's same-key path deliberately keeps the existing TableClient, so the
     * client is recycled explicitly to establish a fresh proxy subscription. Recovery diagnostics
     * are aggregated per TableModel so event bursts remain observable without producing one log per
     * wire message.
     */
    async #recoverFromEndOfData(): Promise<void> {
        const staleKey = this.#state.tableCreationResult?.key;
        const epoch = this.#endOfDataRecoveryEpoch;

        if (this.#endOfDataRecoveryInFlightEpoch === epoch) {
            return;
        }

        if (!this.#query) {
            this.#resetEndOfDataRecovery('no-query');
            return;
        }

        this.#endOfDataRecoveryInFlightEpoch = epoch;
        this.#endOfDataRecoveryAttempts += 1;
        const recoveryAttempt = this.#endOfDataRecoveryAttempts;
        this.#endOfDataRecoveryLog.update({ type: 'recovery-attempt', attempt: recoveryAttempt });
        let shouldRetry = false;
        try {
            const result = await this.#query.refetch();

            // The refetch is a real network round trip; the widget may have been closed meanwhile.
            // Recycling after disposal would construct a TableClient (and a live proxy
            // subscription) into the already-disposed box, which nothing would ever dispose.
            if (this.#destroyed || this.disposableStack.disposed || epoch !== this.#endOfDataRecoveryEpoch) {
                return;
            }

            if (!result.isSuccess || !result.data) {
                this.#endOfDataRecoveryLog.update({
                    type: 'refetch-failed',
                    failure: 'result',
                    error: result.error ?? undefined,
                });
                shouldRetry = true;
                return;
            }

            if (staleKey && compareKey(staleKey, result.data.key)) {
                // Same key: the query subscriber's same-key path keeps the existing TableClient,
                // whose proxy subscription is the one that just ended. Recycle it (with the
                // freshly fetched creation result, whose table cookie is the newest) so a fresh
                // subscription is established.
                const recoveryQueryTiming =
                    this.#endOfDataRecoveryQueryTiming?.epoch === epoch
                        ? this.#endOfDataRecoveryQueryTiming.timing
                        : undefined;
                this.#endOfDataRecoveryQueryTiming = undefined;
                this.#takeStateAction({
                    type: 'recycleTableClient',
                    tableCreationResult: result.data,
                    tableQueryTiming: recoveryQueryTiming,
                });
                this.#endOfDataRecoveryLog.update({ type: 'replacement-created', result: 'same-key' });
            } else {
                // A different key means the refetch already replaced the TableClient through the
                // normal receiveTableCreationResult path; nothing further to recycle.
                this.#endOfDataRecoveryLog.update({
                    type: 'replacement-created',
                    result: 'new-key',
                    newTableKey: stringifyKey(result.data.key),
                });
            }
            if (this.tableClient) {
                this.#armEndOfDataRecoveryStableTimer(this.tableClient);
            }
        } catch (error) {
            if (!this.#destroyed && !this.disposableStack.disposed && epoch === this.#endOfDataRecoveryEpoch) {
                this.#endOfDataRecoveryLog.update({
                    type: 'refetch-failed',
                    failure: 'threw',
                    error: error instanceof Error ? error : new Error(String(error)),
                });
                shouldRetry = true;
            }
        } finally {
            if (this.#endOfDataRecoveryInFlightEpoch === epoch) {
                this.#endOfDataRecoveryInFlightEpoch = undefined;
            }
            if (
                shouldRetry &&
                !this.#destroyed &&
                !this.disposableStack.disposed &&
                epoch === this.#endOfDataRecoveryEpoch
            ) {
                this.#scheduleEndOfDataRecovery('refetch-failed');
            }
        }
    }

    get error() {
        return this.#state.error;
    }

    getColumnById(columnId: string): OutputColumnWithMetadata | null {
        return this.#columnByIdMap.get(columnId) ?? null;
    }

    getColumnUpdate$(columnId: string) {
        // this.#log.trace({ message: 'TableClient is not null, returning update$', columnId });
        return this.update$.pipe(map((update) => update[columnId]));
    }

    init(tableCreationOptions: TableCreationOptions) {
        this.#destroyed = false;
        if (TRACE) {
            this.#log.trace({
                message: 'init() called',
                isSignal: tableCreationOptions.queryOptions instanceof Signal,
                queryOptions:
                    tableCreationOptions.queryOptions instanceof Signal
                        ? tableCreationOptions.queryOptions.value
                        : tableCreationOptions.queryOptions,
            });
        }

        const devtoolsName = tableCreationOptions.devtoolsName ?? this.#pendingDevtoolsName;
        this.#debugIdentifier = devtoolsName;
        const devtoolsInstanceName = devtoolsName
            ? `TableModel: ${devtoolsName} [${this.#id}]`
            : `TableModel [${this.#id}]`;
        this.#devtools = connectReducerDevtools({ name: devtoolsInstanceName });
        if (this.#devtools) {
            this.disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state);
        }

        // Always use a signal internally - either the one passed in or create one
        this.#queryOptionsSignal =
            tableCreationOptions.queryOptions instanceof Signal
                ? tableCreationOptions.queryOptions
                : signal(tableCreationOptions.queryOptions);

        // Proxy selection does not affect table creation; it only selects the transport used by the
        // TableClient after the query produces a TCR.
        const initialOptions = this.#createOptimizedQueryOptions(this.#queryOptionsSignal.value);
        this.#takeStateAction({ type: 'updateQueryOptions', queryOptions: initialOptions });
        this.#query = new QueryObserver(this.queryClient, initialOptions);

        // Query options react only to caller-owned table-creation inputs. Proxy target changes are
        // handled independently by the reducer and do not touch the QueryObserver.
        this.disposableStack.defer(
            effect(() => {
                const newOptions = this.#createOptimizedQueryOptions(this.#queryOptionsSignal.value);
                untracked(() => {
                    if (hashKey(this.#state.queryOptions.queryKey) !== hashKey(newOptions.queryKey)) {
                        this.#resetEndOfDataRecovery('query-options-changed');
                    }
                    if (TRACE) {
                        this.#log.trace({ message: 'Query options changed', queryOptions: newOptions });
                    }
                    this.#takeStateAction({ type: 'updateQueryOptions', queryOptions: newOptions });

                    // Always update QueryObserver - the wrapped queryFn handles optimization
                    if (this.#query) {
                        this.#query.setOptions(newOptions);
                    }
                });
            }),
        );

        // Arbitrary default value; every location in the product overrides this so it doesn't matter.
        this.#bounds = tableCreationOptions.initBounds || { firstRow: 0, windowSize: 100 };
        this.#throttle = tableCreationOptions.initThrottle;

        const unsubscribe = this.#query.subscribe((queryResult) => {
            if (this.#destroyed) {
                return;
            }
            if (!queryResult.isEnabled) {
                if (TRACE) {
                    this.#log.trace({ message: 'Query disabled, clearing table client' });
                }
                this.#resetEndOfDataRecovery('query-disabled');
                this.#takeStateAction({ type: 'clearTable' });
                this.#failureCountSignal.value = 0;
                return;
            }

            if (queryResult.failureCount !== this.#failureCountSignal.value) {
                if (TRACE) {
                    this.#log.trace({
                        message: 'Query failure count changed',
                        previousFailureCount: this.#failureCountSignal.value,
                        failureCount: queryResult.failureCount,
                    });
                }
                this.#failureCountSignal.value = queryResult.failureCount;
            }

            if (queryResult.isPending) {
                if (TRACE && this.#state.tableStatus !== 'connecting') {
                    this.#log.trace({ message: 'Query pending, entering connecting state' });
                }
                this.#takeStateAction({
                    type: 'updateTableConnectionStatus',
                    status: 'connecting',
                });
                return;
            }

            if (!queryResult.isSuccess) {
                // Recovery failures are summarized by the bounded recovery log aggregator. Normal
                // table-creation failures retain their immediate diagnostic.
                if (this.#endOfDataRecoveryInFlightEpoch !== this.#endOfDataRecoveryEpoch) {
                    this.#log.error({
                        message: 'Table creation failed',
                        error: queryResult.error ?? undefined,
                        queryResult,
                    });
                }

                // Put table into error state which the UI can read
                this.#takeStateAction({
                    type: 'enterErroredState',
                    error: queryResult.error ?? new Error('Unknown Table Creation error'),
                });
                this.#takeStateAction({
                    type: 'updateTableConnectionStatus',
                    status: 'disconnected',
                });
                return;
            }

            // How does this get reached? ... for now we'll re-use error state from above
            if (!queryResult.data) {
                this.#completedQueryFnTimings.delete(hashKey(this.#state.queryOptions.queryKey));
                if (this.#endOfDataRecoveryInFlightEpoch !== this.#endOfDataRecoveryEpoch) {
                    this.#log.error({
                        message: 'Table creation resulted in no data',
                        error: queryResult.error ?? undefined,
                        queryResult,
                    });
                }
                this.#takeStateAction({
                    type: 'updateTableConnectionStatus',
                    status: 'disconnected',
                });

                this.#takeStateAction({ type: 'clearTable' });
                return;
            }

            const { data } = queryResult;
            // TODO: We will set the fetch mode when the proxy supports diffs on X suffix
            //if (
            //    this.tableClient &&
            //    compareKey(this.tableClient.key, data.key) &&
            //    this.tableClient.fetchMode !== data.fetchMode
            //) {
            //    this.tableClient.fetchMode = data.fetchMode;
            //}

            const queryHash = hashKey(this.#state.queryOptions.queryKey);
            const completedQueryFnTiming = this.#completedQueryFnTimings.get(queryHash);
            this.#completedQueryFnTimings.delete(queryHash);
            const tableQueryTiming: TableQueryTiming = {
                queryFnStartedAt: completedQueryFnTiming?.queryFnStartedAt,
                queryFnCompletedAt: completedQueryFnTiming?.queryFnCompletedAt,
                tableRequestStartedAt: completedQueryFnTiming?.tableRequestStartedAt,
                tableRequestCompletedAt: completedQueryFnTiming?.tableRequestCompletedAt,
                // This QueryObserver success callback is the first exact point at which TableModel
                // knows the selected table-creation result it will use.
                tableKnownAt: performance.now(),
            };
            const previousTableClientSpec = this.#state.tableClientSpec;

            this.#takeStateAction({
                type: 'receiveTableCreationResult',
                tableCreationResult: data,
                tableQueryTiming,
            });

            if (
                previousTableClientSpec === this.#state.tableClientSpec &&
                this.#endOfDataRecoveryInFlightEpoch === this.#endOfDataRecoveryEpoch
            ) {
                // The same-key reducer path creates no spec. Recovery carries the timing briefly
                // until #recoverFromEndOfData explicitly recycles the client.
                this.#endOfDataRecoveryQueryTiming = {
                    epoch: this.#endOfDataRecoveryEpoch,
                    timing: tableQueryTiming,
                };
            }

            // TODO: Monitor keys list
            this.#takeStateAction({
                type: 'updateTableConnectionStatus',
                status: 'connected',
            });
        });
        this.disposableStack.defer(unsubscribe);
    }

    get key() {
        return this.tableClient?.key ?? null;
    }

    get serverUUID() {
        return this.#state.serverUUID;
    }

    #id: string;
    #tableStartupSequence = 0;
    #log: ReactBindings['Logger'];
    #endOfDataRecoveryLog: EndOfDataRecoveryLogAggregator;
    #devtools: ReducerDevtoolsConnection<TableState, TableStateAction> | undefined;
    #pendingDevtoolsName: string | undefined;

    #query!: QueryObserver<FullKeyType | OptimizedResultWrapper | undefined, any, FullKeyType | undefined>;

    setDevtoolsName(name: string) {
        this.#debugIdentifier = name;
        if (this.#devtools) {
            this.#devtools.setName(`TableModel: ${name} [${this.#id}]`);
        } else {
            this.#pendingDevtoolsName = name;
        }
    }

    // TODO: Allow bounds to be set to undefined
    setBounds(bounds: Bounds) {
        this.#bounds = bounds;
        if (this.tableClient) {
            this.tableClient.bounds = bounds;
        }
    }

    setThrottle(throttle: number | undefined, getMoreData: boolean): void {
        this.#throttle = throttle;
        if (this.tableClient) {
            this.tableClient.throttle = throttle === undefined ? DEFAULT_THROTTLE_MS : throttle;

            if (getMoreData) {
                this.tableClient.getMoreData();
            }
        }
    }

    async snapshot({ firstRow, windowSize }: { firstRow?: number; windowSize?: number }) {
        if (this.#state.tableCreationResult === null) {
            return null;
        }

        const { key, tableCookie } = this.#state.tableCreationResult;

        if (!tableCookie) {
            return null;
        }

        const useNgProxy = this.#state.proxyTarget?.kind === 'nextgen';
        return getSnapshot(useNgProxy, key, tableCookie, firstRow, windowSize);
    }

    async waitForTableConnection(): Promise<void> {
        using stack = new DisposableStack();

        const dataResolved = deferred<void>();

        stack.defer(
            effect(() => {
                if (this.connectionStatus.value === 'connected') {
                    dataResolved.resolve();
                }
            }),
        );

        const timeout = new Promise<never>((_resolve, reject) => {
            stack.adopt(
                setTimeout(() => {
                    reject(new Error(`Timed out waiting for table connection`));
                }, 10_000),
                clearTimeout,
            );
        });

        return await Promise.race([dataResolved.promise, timeout]);
    }

    get sortable() {
        if (this.#state.tableCreationResult?.key) {
            return this.#state.tableCreationResult.key.ex !== 'M' && this.#state.tableCreationResult.key.ex !== 'S';
        }
        return false;
    }

    #state: TableState = createInitialTableState();

    private get state() {
        return this.#state;
    }

    @reacts private set state(state: TableState) {
        const previousState = this.#state;
        this.#state = state;

        this.#syncTableClientLifecycle(previousState.tableClientSpec, state.tableClientSpec);

        // Sync the column lookup Map
        if (state.columns.length === 0) {
            this.#columnByIdMap.clear();
        } else if (state.columns !== previousState.columns) {
            this.#columnByIdMap = new Map(state.columns.map((column) => [column.id, column]));
        }

        if (this.#columnsSignal.value !== state.columns) {
            this.#columnsSignal.value = state.columns;
        }

        const nextKey = this.tableClient?.key ?? '';
        if (this.#keySignal.value !== nextKey) {
            this.#keySignal.value = nextKey;
        }

        // Update the stored query options mapping when queryOptions change
        // Note: QueryObserver.setOptions is called from init() effect, and the
        // wrapped queryFn handles sort/filter optimization at fetch time
        if (previousState.queryOptions !== state.queryOptions && this.tableClient) {
            this.#tableClientQueryOptionsMap.set(this.tableClient, state.queryOptions);
        }
    }

    #syncTableClientLifecycle(previousSpec: TableClientSpec | null, spec: TableClientSpec | null) {
        if (previousSpec === spec) {
            return;
        }
        if (!spec) {
            this.#tableClientBox.clear();
            return;
        }

        const { proxyTarget, tableCreationResult, tableQueryTiming } = spec;

        if (TRACE) {
            this.#log.trace({
                message: 'Creating new TableClient',
                key: stringifyKey(tableCreationResult.key),
                proxyGeneration: proxyTarget.generation,
            });
        }
        this.#tableClientBox.next(() => {
            const tableClientCreationStartedAt = performance.now();
            // Configure bounds before assigning the key. With immediate bounds handling, creating
            // an already-keyed client on a connected transport would first subscribe with the
            // client's arbitrary fallback window and immediately replace it with #bounds.
            const tableClient = new TableClient(proxyTarget.client, undefined, this.#log);
            const tableClientConstructedAt = performance.now();
            tableClient.accessCookie = tableCreationResult.tableCookie;
            // TODO: We will set the fetch mode when the proxy supports diffs on X suffix
            //tableClient.fetchMode = tableCreationResult.fetchMode;
            tableClient.bounds = this.#bounds;
            tableClient.throttle = this.#throttle === undefined ? DEFAULT_THROTTLE_MS : this.#throttle;
            const tableClientConfiguredAt = performance.now();

            let startupLatency: TableStartupLatency | undefined;
            if (tableQueryTiming) {
                this.#tableStartupSequence += 1;
                const timing: TableStartupTiming = {
                    ...tableQueryTiming,
                    tableClientCreationStartedAt,
                    tableClientConstructedAt,
                    tableClientConfiguredAt,
                };
                startupLatency = new TableStartupLatency(
                    this.#log,
                    {
                        debugIdentifier: this.#debugIdentifier,
                        proxyGeneration: proxyTarget.generation,
                        proxyKind: proxyTarget.kind,
                        tableKey: stringifyKey(tableCreationResult.key),
                        tableModelId: this.#id,
                        tableStartupId: `${this.#id}:${this.#tableStartupSequence}`,
                    },
                    timing,
                );
                startupLatency.observe(tableClient);
            }

            tableClient.key = stringifyKey(tableCreationResult.key);
            if (startupLatency && tableClient.subscription !== undefined) {
                // Positive bounds emit dataRequest$ during key assignment. This fallback covers a
                // valid zero-sized window, where RawClient.subscribe() occurs without a data prime.
                startupLatency.markSubscribed();
            }

            const tsIdConnection = isConnectableStream(tableClient.tsId$) ? tableClient.tsId$.connect() : undefined;

            if (TRACE) {
                this.#log.trace({
                    message: 'Configured new TableClient',
                    bounds: this.#bounds,
                    throttle: this.#throttle,
                    tableClientKey: tableClient.key,
                });
                if (tsIdConnection) {
                    this.#log.trace({ message: 'Connected tsId$ observable' });
                }
            }

            return new TableClientEnvelope(tableClient, tsIdConnection, startupLatency);
        });
        if (this.tableClient) {
            this.#tableClientQueryOptionsMap.set(this.tableClient, this.#state.queryOptions);
        }
    }

    get tableClient() {
        return this.#tableClientBox.current?.tableClient ?? null;
    }

    #takeStateAction(action: TableStateAction) {
        const nextState = tableStateReducer(this.state, action);
        if (nextState !== this.state) {
            this.state = nextState;
            this.#updateConnectionStatusSignal();
            this.#devtools?.send(action, this.#state);
        }
    }

    #throttle: number | undefined;

    get throttle() {
        return this.#throttle;
    }

    #updateConnected$ = new BehaviorSubject<RowUpdate | typeof NEW_TABLE_RESET>(NEW_TABLE_RESET);
    #updatesConnected$: Connectable<ObservedValueOf<TableClient['updates$']> | NEW_TABLE_RESET>;
    #update$: TableClient['update$'];
    #updates$: TableClient['updates$'];

    get update$(): TableClient['update$'] {
        return this.#update$;
    }

    get updates$(): TableClient['updates$'] {
        return this.#updates$;
    }

    #updateSignal!: Signal<SignalUpdate>;
    #countSignal!: Signal<number>;
    get updateSignal(): ReadonlySignal<SignalUpdate> {
        if (this.#updateSignal) return this.#updateSignal;

        this.#updateSignal = signal<SignalUpdate>({
            update: this.#updateConnected$.value === NEW_TABLE_RESET ? undefined : this.#updateConnected$.value,
        });

        let update: SignalUpdate = { update: undefined };
        let lastUpdate: SignalUpdate = { update: undefined };

        this.disposableStack.use(
            this.#updateConnected$.pipe(skip(1)).subscribe((tableUpdate) => {
                const tmp = update;
                update = lastUpdate;
                lastUpdate = tmp;

                update.update = tableUpdate === NEW_TABLE_RESET ? undefined : tableUpdate;

                this.#updateSignal.value = update;
            }),
        );

        return this.#updateSignal;
    }

    get countSignal(): ReadonlySignal<number> {
        if (this.#countSignal) return this.#countSignal;

        // Initialize with 0 since count$ starts with NEW_TABLE_RESET
        this.#countSignal = signal<number>(0);

        this.disposableStack.use(
            this.#countConnected$.subscribe((count) => {
                this.#countSignal.value = (count as any) === NEW_TABLE_RESET ? 0 : count;
            }),
        );

        return this.#countSignal;
    }

    get updateWithReset$(): Observable<ObservedValueOf<TableClient['update$']> | NEW_TABLE_RESET> {
        return this.#updateConnected$;
    }

    get updatesWithReset$(): Observable<ObservedValueOf<TableClient['updates$']> | NEW_TABLE_RESET> {
        return this.#updatesConnected$;
    }

    /**
     * @deprecated Prefer the Signal form of `queryOptions` in `init()` for reactivity.
     * This imperative API exists for legacy code that cannot easily adopt signals.
     */
    updateQueryOptions(queryOptions: QueryObserverOptions<any, any, FullKeyType>) {
        if (TRACE) {
            this.#log.trace({
                message: 'updateQueryOptions called directly',
                queryOptions,
            });
        }
        // Simply update the signal - the effect handles everything else
        this.#queryOptionsSignal.value = queryOptions;
    }
}
